import type { LanguageLevel } from "@/components/fullscreen/PracticeLevelSelector";
import { buildExtendedImpromptuPrompts } from "./extended-prompt-library";
import { buildAdditionalImpromptuPrompts } from "./prompt-library";

export const IMPROMPTU_SCHEMA_VERSION = "2" as const;
export const IMPROMPTU_RUBRIC_VERSION = "2026-09-02.2" as const;
export const IMPROMPTU_TEXT_MODEL = "gpt-6-luna" as const;
// This model is retained only for requests containing learner audio.
export const IMPROMPTU_GEMINI_AUDIO_MODEL = "gemini-3.5-flash-lite" as const;
export const IMPROMPTU_GEMINI_TRANSCRIPTION_MODEL = "gemini-3.5-transcribe" as const;
export const IMPROMPTU_SONIOX_MODEL = "stt-async-v5" as const;
export const IMPROMPTU_TRANSCRIPTION_MODES = [
  "soniox_with_gemini_fallback",
  "soniox_only",
  "gemini_only",
] as const;
export type ImpromptuTranscriptionMode = (typeof IMPROMPTU_TRANSCRIPTION_MODES)[number];

/**
 * The analyze route runs transcription and analysis in series inside a single
 * serverless invocation, so both timeouts plus request handling have to fit
 * inside its `maxDuration`. Keep these three numbers in step; the budget is
 * asserted in `config.test.ts`.
 */
export const IMPROMPTU_ROUTE_BUDGET_SECONDS = 120;

/**
 * How much faster than realtime we assume Soniox async batch transcription runs.
 *
 * This is an assumption, not a measurement. It lives here as a named constant so
 * the timeout below has a stated justification instead of being a round number,
 * and so the thing to re-derive is obvious once there is production data: replace
 * it with an observed p95 and let the assertion in `config.test.ts` tell you
 * whether the timeout still covers the longest recording we accept.
 */
export const IMPROMPTU_ASSUMED_TRANSCRIPTION_SPEEDUP = 3;

/**
 * 32s rather than a rounder 30s because 30s is *exactly* the assumed worst case
 * for a 90-second recording, and "speak for up to 90 seconds" invites recordings
 * at the ceiling. Zero margin over an unmeasured assumption means a full-length
 * attempt fails on a slow day for a learner who did nothing wrong.
 */
export const IMPROMPTU_TRANSCRIPTION_TIMEOUT_MS = 32_000;
export const IMPROMPTU_GEMINI_TRANSCRIPTION_TIMEOUT_MS = 20_000;
export const IMPROMPTU_ANALYSIS_TIMEOUT_MS = 20_000;
/**
 * Audio gets a shorter first attempt so a failed multimodal request can fall
 * back to the established transcript-only call inside the route budget.
 */
export const IMPROMPTU_GEMINI_AUDIO_TIMEOUT_MS = 12_000;
// Conversion precedes Azure; retain eight seconds of route handling headroom.
export const IMPROMPTU_CONVERSION_TIMEOUT_MS = 5_000;
export const IMPROMPTU_PRONUNCIATION_TIMEOUT_MS = 75_000;

/**
 * Prompt generation happens while the learner is waiting to start, so a hung
 * provider call is worse here than a missing prompt: the library always has a
 * fallback. This must stay under the generate-prompt route's own `maxDuration`.
 */
export const IMPROMPTU_PROMPT_GENERATION_BUDGET_SECONDS = 20;
export const IMPROMPTU_PROMPT_GENERATION_TIMEOUT_MS = 12_000;

export const IMPROMPTU_MIN_DURATION_SECONDS = 5;
export const IMPROMPTU_MAX_DURATION_SECONDS = 90;
// Kept on prompt records for transport compatibility. Recording starts as soon
// as microphone permission is granted; learners can think before pressing Start.
export const IMPROMPTU_PREP_SECONDS = 0;
/**
 * How long an answer is meant to take, as opposed to how long we will let it run.
 * Every prompt carries it, the chooser shows it, and the prompt-writing brief in
 * `rubric.ts` is written around it, so it was previously the literal 60 in about
 * fifty places. `IMPROMPTU_MAX_DURATION_SECONDS` is the recording ceiling and is
 * deliberately higher: someone who needs ninety seconds is not failing.
 */
export const IMPROMPTU_TARGET_SECONDS = 60;
export const IMPROMPTU_MAX_AUDIO_BYTES = 15 * 1024 * 1024;
// Conservative decimal-byte budgets, including multipart fields and boundaries.
export const IMPROMPTU_MAX_UPLOAD_BODY_BYTES = 4_300_000;
export const IMPROMPTU_MAX_ORIGINAL_UPLOAD_BYTES = 4_000_000;
export const IMPROMPTU_RECORDING_BITRATE = 64_000;
// Inline base64 must leave room under Gemini's 20 MB request ceiling.
export const IMPROMPTU_MAX_GEMINI_TRANSCRIPTION_AUDIO_BYTES = 12 * 1024 * 1024;
export const IMPROMPTU_MAX_PRONUNCIATION_AUDIO_BYTES = 4 * 1024 * 1024;
export const IMPROMPTU_MIN_PROMPT_CHARACTERS = 12;
export const IMPROMPTU_MAX_PROMPT_CHARACTERS = 220;
export const IMPROMPTU_PRONUNCIATION_VERSION = "2" as const;

export const IMPROMPTU_LANGUAGES = [
  { code: "en", name: "English", locale: "en-US" },
  { code: "es", name: "Spanish", locale: "es-ES" },
  { code: "fr", name: "French", locale: "fr-FR" },
  { code: "nl", name: "Dutch", locale: "nl-NL" },
  { code: "de", name: "German", locale: "de-DE" },
  { code: "it", name: "Italian", locale: "it-IT" },
  { code: "pt", name: "Portuguese (Brazil)", locale: "pt-BR" },
  { code: "ja", name: "Japanese", locale: "ja-JP" },
  { code: "ko", name: "Korean", locale: "ko-KR" },
  { code: "zh", name: "Mandarin Chinese", locale: "zh-CN" },
  { code: "hi", name: "Hindi", locale: "hi-IN" },
  { code: "pl", name: "Polish", locale: "pl-PL" },
  { code: "ru", name: "Russian", locale: "ru-RU" },
  { code: "sv", name: "Swedish", locale: "sv-SE" },
] as const;

export type ImpromptuLanguageCode = (typeof IMPROMPTU_LANGUAGES)[number]["code"];
export type ImpromptuLearnerProfile = LanguageLevel | "fluent_native";

/**
 * The levels offered in the settings control, and the source of truth for which
 * strings are valid learner profiles.
 *
 * Each option carried a `description` — "Support your view and connect your
 * ideas", and four more like it — that nothing ever rendered. They read as level
 * guidance a learner would find useful, so their absence from the interface was
 * easy to miss, and it would have been fair to assume they were being applied
 * somewhere. What the level actually changes is the rubric the model grades
 * against, which is written where the model reads it.
 */
export const IMPROMPTU_PROFILE_OPTIONS: ReadonlyArray<{
  value: ImpromptuLearnerProfile;
  label: string;
}> = [
  { value: "absolute_beginner", label: "First steps" },
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "fluent_native", label: "Fluent or native" },
];

export const IMPROMPTU_PROMPT_CATEGORIES = [
  { value: "opinion", label: "Opinion" },
  { value: "story", label: "Story" },
  { value: "explain", label: "Explain" },
  { value: "persuade", label: "Persuade" },
  { value: "reflect", label: "Reflect" },
  { value: "everyday", label: "Everyday life" },
  { value: "work", label: "Work & study" },
  { value: "imagine", label: "Imagine" },
] as const;

export type ImpromptuPromptCategory = (typeof IMPROMPTU_PROMPT_CATEGORIES)[number]["value"];
export type ImpromptuPromptSource = "curated" | "generated" | "custom";

export type ImpromptuPrompt = {
  id: string;
  category: ImpromptuPromptCategory;
  languageCode: ImpromptuLanguageCode;
  text: string;
  preparationSeconds: number;
  targetSeconds: number;
};

export type ImpromptuPromptSelection = ImpromptuPrompt & {
  source: ImpromptuPromptSource;
};

const CORE_IMPROMPTU_PROMPTS: ReadonlyArray<ImpromptuPrompt> = [
  {
    id: "opinion-meeting-free-day-en",
    category: "opinion",
    languageCode: "en",
    text: "Should people have one meeting-free day each week? Explain your view.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "opinion-city-centre-cars-en",
    category: "opinion",
    languageCode: "en",
    text: "Should city centres have fewer cars? Explain who would benefit and what might become harder.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-useful-mistake-en",
    category: "story",
    languageCode: "en",
    text: "Tell the story of a small mistake that taught you something useful.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-stranger-changed-day-en",
    category: "story",
    languageCode: "en",
    text: "Describe a time a stranger changed your day.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-culture-en",
    category: "explain",
    languageCode: "en",
    text: "Explain a part of your culture that visitors often misunderstand.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-welcome-guest-en",
    category: "explain",
    languageCode: "en",
    text: "Explain how to make a guest feel welcome in your home.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-activity-en",
    category: "persuade",
    languageCode: "en",
    text: "Convince a friend to try an activity you enjoy.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-quiet-spaces-en",
    category: "persuade",
    languageCode: "en",
    text: "Convince your town to create more quiet public spaces.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-helpful-habit-en",
    category: "reflect",
    languageCode: "en",
    text: "What habit has improved your daily life, and why has it worked for you?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-small-decision-en",
    category: "reflect",
    languageCode: "en",
    text: "Which small decision had a bigger effect than you expected?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "opinion-meeting-free-day-es",
    category: "opinion",
    languageCode: "es",
    text: "¿Debería haber un día sin reuniones cada semana? Explica tu opinión.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "opinion-city-centre-cars-es",
    category: "opinion",
    languageCode: "es",
    text: "¿Debería haber menos coches en el centro de las ciudades? Explica quién se beneficiaría y qué podría ser más difícil.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-useful-mistake-es",
    category: "story",
    languageCode: "es",
    text: "Cuenta la historia de un pequeño error que te enseñó algo útil.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-stranger-changed-day-es",
    category: "story",
    languageCode: "es",
    text: "Describe una ocasión en la que una persona desconocida cambió tu día.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-culture-es",
    category: "explain",
    languageCode: "es",
    text: "Explica un aspecto de tu cultura que los visitantes suelen malinterpretar.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-welcome-guest-es",
    category: "explain",
    languageCode: "es",
    text: "Explica cómo hacer que un invitado se sienta bienvenido en tu casa.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-activity-es",
    category: "persuade",
    languageCode: "es",
    text: "Convence a un amigo para que pruebe una actividad que te gusta.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-quiet-spaces-es",
    category: "persuade",
    languageCode: "es",
    text: "Convence a tu ciudad de crear más espacios públicos tranquilos.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-helpful-habit-es",
    category: "reflect",
    languageCode: "es",
    text: "¿Qué hábito ha mejorado tu vida diaria y por qué te ha funcionado?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-small-decision-es",
    category: "reflect",
    languageCode: "es",
    text: "¿Qué pequeña decisión tuvo un efecto mayor de lo que esperabas?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "opinion-meeting-free-day-fr",
    category: "opinion",
    languageCode: "fr",
    text: "Devrait-on avoir une journée sans réunions chaque semaine ? Expliquez votre point de vue.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "opinion-city-centre-cars-fr",
    category: "opinion",
    languageCode: "fr",
    text: "Devrait-il y avoir moins de voitures dans les centres-villes ? Expliquez qui en profiterait et ce qui pourrait devenir plus difficile.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-useful-mistake-fr",
    category: "story",
    languageCode: "fr",
    text: "Racontez une petite erreur qui vous a appris quelque chose d'utile.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-stranger-changed-day-fr",
    category: "story",
    languageCode: "fr",
    text: "Décrivez une occasion où une personne inconnue a changé votre journée.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-culture-fr",
    category: "explain",
    languageCode: "fr",
    text: "Expliquez un aspect de votre culture que les visiteurs comprennent souvent mal.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-welcome-guest-fr",
    category: "explain",
    languageCode: "fr",
    text: "Expliquez comment faire en sorte qu'un invité se sente bienvenu chez vous.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-activity-fr",
    category: "persuade",
    languageCode: "fr",
    text: "Convainquez un ami d'essayer une activité que vous aimez.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-quiet-spaces-fr",
    category: "persuade",
    languageCode: "fr",
    text: "Convainquez votre ville de créer davantage d'espaces publics calmes.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-helpful-habit-fr",
    category: "reflect",
    languageCode: "fr",
    text: "Quelle habitude a amélioré votre quotidien, et pourquoi a-t-elle fonctionné pour vous ?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-small-decision-fr",
    category: "reflect",
    languageCode: "fr",
    text: "Quelle petite décision a eu un effet plus important que prévu ?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "opinion-meeting-free-day-nl",
    category: "opinion",
    languageCode: "nl",
    text: "Zou er elke week een vergadervrije dag moeten zijn? Leg je mening uit.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "opinion-city-centre-cars-nl",
    category: "opinion",
    languageCode: "nl",
    text: "Zouden er minder auto's in stadscentra moeten zijn? Leg uit wie daarvan profiteert en wat moeilijker kan worden.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-useful-mistake-nl",
    category: "story",
    languageCode: "nl",
    text: "Vertel het verhaal van een kleine fout waarvan je iets nuttigs hebt geleerd.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "story-stranger-changed-day-nl",
    category: "story",
    languageCode: "nl",
    text: "Beschrijf een moment waarop een onbekende je dag veranderde.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-culture-nl",
    category: "explain",
    languageCode: "nl",
    text: "Leg een aspect van je cultuur uit dat bezoekers vaak verkeerd begrijpen.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "explain-welcome-guest-nl",
    category: "explain",
    languageCode: "nl",
    text: "Leg uit hoe je ervoor zorgt dat een gast zich welkom voelt in jouw huis.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-activity-nl",
    category: "persuade",
    languageCode: "nl",
    text: "Overtuig een vriend om een activiteit te proberen die jij leuk vindt.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "persuade-quiet-spaces-nl",
    category: "persuade",
    languageCode: "nl",
    text: "Overtuig je gemeente om meer rustige openbare plekken te creëren.",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-helpful-habit-nl",
    category: "reflect",
    languageCode: "nl",
    text: "Welke gewoonte heeft je dagelijks leven verbeterd, en waarom werkt die voor jou?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
  {
    id: "reflect-small-decision-nl",
    category: "reflect",
    languageCode: "nl",
    text: "Welke kleine beslissing had meer effect dan je had verwacht?",
    preparationSeconds: IMPROMPTU_PREP_SECONDS,
    targetSeconds: IMPROMPTU_TARGET_SECONDS,
  },
];

export const IMPROMPTU_PROMPTS: ReadonlyArray<ImpromptuPrompt> = [
  ...CORE_IMPROMPTU_PROMPTS,
  ...buildAdditionalImpromptuPrompts(IMPROMPTU_PREP_SECONDS, IMPROMPTU_TARGET_SECONDS),
  ...buildExtendedImpromptuPrompts(IMPROMPTU_PREP_SECONDS, IMPROMPTU_TARGET_SECONDS),
];

export function isImpromptuPromptCategory(value: string): value is ImpromptuPromptCategory {
  return IMPROMPTU_PROMPT_CATEGORIES.some((category) => category.value === value);
}

export function isImpromptuPromptSource(value: string): value is ImpromptuPromptSource {
  return value === "curated" || value === "generated" || value === "custom";
}

export function normalizeImpromptuPromptText(value: string) {
  return value
    .replace(/[\u0000-\u001F\u007F]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isValidImpromptuPromptText(value: string) {
  const normalized = normalizeImpromptuPromptText(value);
  return (
    normalized.length >= IMPROMPTU_MIN_PROMPT_CHARACTERS &&
    normalized.length <= IMPROMPTU_MAX_PROMPT_CHARACTERS
  );
}

export function isImpromptuLanguageCode(value: string): value is ImpromptuLanguageCode {
  return IMPROMPTU_LANGUAGES.some((language) => language.code === value);
}

export function resolveImpromptuLanguageCode(
  value: string | null | undefined
): ImpromptuLanguageCode | null {
  const normalized = value?.trim().toLowerCase().replace(/_/g, "-");
  if (!normalized) return null;

  const exactMatch = IMPROMPTU_LANGUAGES.find(
    (language) =>
      language.code.toLowerCase() === normalized || language.locale.toLowerCase() === normalized
  );
  if (exactMatch) return exactMatch.code;

  const [baseCode] = normalized.split("-");
  return IMPROMPTU_LANGUAGES.find((language) => language.code === baseCode)?.code ?? null;
}

export function isImpromptuLearnerProfile(value: string): value is ImpromptuLearnerProfile {
  return IMPROMPTU_PROFILE_OPTIONS.some((profile) => profile.value === value);
}

export function getImpromptuLanguage(code: string) {
  return IMPROMPTU_LANGUAGES.find((language) => language.code === code);
}

export function getImpromptuPrompt(promptId: string, languageCode: ImpromptuLanguageCode) {
  return IMPROMPTU_PROMPTS.find(
    (prompt) => prompt.id === promptId && prompt.languageCode === languageCode
  );
}

export function getImpromptuPrompts(languageCode: ImpromptuLanguageCode) {
  return IMPROMPTU_PROMPTS.filter((prompt) => prompt.languageCode === languageCode);
}

export function toImpromptuPromptSelection(prompt: ImpromptuPrompt): ImpromptuPromptSelection {
  return { ...prompt, source: "curated" };
}

export function isImpromptuClientEnabled() {
  return process.env.NEXT_PUBLIC_IMPROMPTU_ENABLED !== "false";
}

export function isImpromptuPronunciationClientEnabled() {
  return process.env.NEXT_PUBLIC_IMPROMPTU_PRONUNCIATION_ENABLED === "true";
}

export function isImpromptuGeminiAudioClientEnabled() {
  return process.env.NEXT_PUBLIC_IMPROMPTU_GEMINI_AUDIO_ENABLED === "true";
}

export function getImpromptuTranscriptionMode(
  value = process.env.IMPROMPTU_TRANSCRIPTION_MODE
): ImpromptuTranscriptionMode {
  return IMPROMPTU_TRANSCRIPTION_MODES.includes(value as ImpromptuTranscriptionMode)
    ? (value as ImpromptuTranscriptionMode)
    : "soniox_with_gemini_fallback";
}
